import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Users, FileText, CalendarDays, Plus, Clock,
  CheckCircle2, StickyNote,
} from 'lucide-react';
import useResponsive from '../../hooks/useResponsive';
import { isRtl } from '../../i18n/direction';

/**
 * TherapyDashboardPanel — primary content for centerType === 'therapy'.
 *
 * Focuses on meetings + clients (vs. lessons). Each widget carries a stable
 * data-testid so the Playwright spec in tests/therapy/11-therapy-dashboard.spec.ts
 * can assert against it.
 *
 * Phase 1: pendingNotes is now real (computed from past meetings without
 * notes) and the card is clickable — it opens AddNoteBottomSheet pre-
 * selected on the most-recent unwritten meeting.
 */
export default function TherapyDashboardPanel({
  todayMeetings = [],
  upcomingMeetings = [],
  clients = [],
  pendingNotes = [],
  onScheduleNewMeeting,
  onAddNoteForMeeting,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rtl = isRtl(i18n.language);
  const { isMobile } = useResponsive();

  const activeClientCount = useMemo(
    () => clients.filter((c) => c.isActive !== false).length,
    [clients]
  );

  const upcomingWeek = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAhead = new Date(today);
    weekAhead.setDate(weekAhead.getDate() + 7);
    return upcomingMeetings.filter((m) => {
      const d = new Date(m.date);
      return d >= today && d <= weekAhead;
    });
  }, [upcomingMeetings]);

  const cardStyle = {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    padding: isMobile ? '1rem' : '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    direction: rtl ? 'rtl' : 'ltr',
  };

  const sectionTitle = (text) => (
    <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem 0' }}>{text}</h3>
  );

  const now = Date.now();

  // Per-meeting state used to drive the right CTA in today's list.
  // A meeting is "past" once its end time (or start+60min) has elapsed.
  function meetingState(m) {
    const d = new Date(m.date);
    if (Number.isNaN(d.getTime())) return 'upcoming';
    let endMs = d.getTime();
    if (m.endTime) {
      const [h, mm] = m.endTime.split(':').map(Number);
      endMs = d.getTime() + (h * 60 + (mm || 0)) * 60 * 1000;
    } else if (m.time) {
      const [h, mm] = m.time.split(':').map(Number);
      endMs = d.getTime() + (h * 60 + (mm || 0) + 60) * 60 * 1000;
    }
    if (endMs > now) return 'upcoming';

    // Past — does it already have notes?
    const c = (m.content || '').toString().trim();
    if (!c) return 'awaitingNotes';
    try {
      const parsed = JSON.parse(c);
      if (parsed && typeof parsed === 'object') {
        const hasNotes = ((parsed.notes || '').toString().trim() || (parsed.nextSteps || '').toString().trim());
        return hasNotes ? 'done' : 'awaitingNotes';
      }
    } catch { /* plain text */ }
    return 'done';
  }

  return (
    <div
      data-testid="therapy-dashboard-panel"
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: isMobile ? '0.5rem' : '0.75rem',
        marginBottom: '1rem',
      }}
    >
      {/* Today's meetings — spans full row on mobile, half on desktop */}
      <div
        data-testid="therapy-today-meetings"
        style={{ ...cardStyle, gridColumn: isMobile ? '1 / -1' : 'span 2' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <Calendar size={18} />
          {sectionTitle(t('therapy.dashboard.todayMeetings'))}
        </div>
        {todayMeetings.length === 0 ? (
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            <p style={{ margin: '0 0 0.75rem 0' }}>{t('therapy.dashboard.noMeetingsToday')}</p>
            <button
              data-testid="therapy-schedule-new-meeting-cta"
              onClick={() => (onScheduleNewMeeting ? onScheduleNewMeeting() : navigate('/calendar'))}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 0.875rem',
                border: 'none',
                borderRadius: '8px',
                background: 'var(--color-primary, #0ea5e9)',
                color: 'var(--color-on-primary, #fff)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                minHeight: '44px',
              }}
            >
              <Plus size={16} />
              {t('therapy.quickActions.newMeeting')}
            </button>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {todayMeetings.slice(0, 4).map((m, i) => {
              const state = meetingState(m);
              return (
                <li
                  key={m.id || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--color-border, #e5e7eb)',
                  }}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)',
                    minWidth: '54px',
                  }}>
                    <Clock size={12} />{m.time || '—'}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.studentName || m.clientName || m.title || '—'}
                  </span>
                  {state === 'awaitingNotes' && onAddNoteForMeeting && (
                    <button
                      data-testid={`therapy-add-note-row-${m.id || i}`}
                      onClick={() => onAddNoteForMeeting(m)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        border: 'none',
                        backgroundColor: 'rgba(14, 165, 233, 0.12)',
                        color: '#0ea5e9',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <StickyNote size={12} />
                      {t('therapy.dashboard.addNotes')}
                    </button>
                  )}
                  {state === 'done' && (
                    <CheckCircle2 size={16} color="#22c55e" aria-label="done" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Active clients KPI */}
      <div
        data-testid="therapy-active-clients-kpi"
        style={{ ...cardStyle, cursor: 'pointer' }}
        onClick={() => navigate('/students')}
        role="button"
        tabIndex={0}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <Users size={18} />
          {sectionTitle(t('therapy.dashboard.activeClients'))}
        </div>
        <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>{activeClientCount}</p>
      </div>

      {/* Pending clinical notes — clickable when there are any */}
      <div
        data-testid="therapy-pending-notes"
        style={{
          ...cardStyle,
          cursor: pendingNotes.length > 0 && onAddNoteForMeeting ? 'pointer' : 'default',
          borderInlineStart: pendingNotes.length > 0 ? '3px solid #f59e0b' : undefined,
        }}
        onClick={() => {
          if (pendingNotes.length > 0 && onAddNoteForMeeting) {
            onAddNoteForMeeting(pendingNotes[0]);
          }
        }}
        role={pendingNotes.length > 0 ? 'button' : undefined}
        tabIndex={pendingNotes.length > 0 ? 0 : -1}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <FileText size={18} />
          {sectionTitle(t('therapy.dashboard.pendingClinicalNotes'))}
        </div>
        <p style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>{pendingNotes.length}</p>
        {pendingNotes.length > 0 && (
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
            {t('therapy.dashboard.pendingNotesHint')}
          </p>
        )}
      </div>

      {/* Upcoming week */}
      <div
        data-testid="therapy-upcoming-week"
        style={{ ...cardStyle, cursor: 'pointer' }}
        onClick={() => navigate('/calendar')}
        role="button"
        tabIndex={0}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <CalendarDays size={18} />
          {sectionTitle(t('therapy.dashboard.upcomingWeek'))}
        </div>
        <p style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>{upcomingWeek.length}</p>
      </div>
    </div>
  );
}
